import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { fmtMoney } from '../shared/format'

/**
 * Döviz, altın ve vadeli hesaplar. Hesap birimi TRY, USD, EUR, GBP, CHF ya da gram altın (XAU). Muhasebe TL ile tutulur:
 * döviz hareketi işlem günü kuruyla TL'ye çevrilir, ay sonu kur değerlemesi farkı kambiyo kârı/zararı olur.
 */
export const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP', 'CHF', 'XAU'] as const
export type Currency = (typeof CURRENCIES)[number]
export const FX_RATE_MANAGE = 'finance:fx_rate:manage'
export const FX_REVALUE = 'finance:fx:revalue'
export const DEPOSIT_READ = 'finance:deposit:read'
export const DEPOSIT_MANAGE = 'finance:deposit:manage'
export const DEPOSIT_SETTINGS = 'finance:deposit:settings'

const SUFFIX: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£', CHF: 'CHF', XAU: 'gr' }
export const currencySuffix = (c: string) => SUFFIX[c] ?? c

/** Tutar biçimi: altın gram, diğerleri para birimiyle. */
export function fmtAmount(amount: string, currency = 'TRY') {
  if (currency === 'XAU') {
    const n = Number(amount)
    return Number.isFinite(n) ? `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 }).format(n)} gr` : amount
  }
  return fmtMoney(amount, currency || 'TRY')
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const optS = (v: unknown) => (v == null ? null : String(v))

export type FxRate = { currency: string; date: string; source: string; buying: string; selling: string }
const mapRate = (r: Row): FxRate => ({ currency: s(r.currency), date: s(r.date), source: s(r.source), buying: s(r.buying), selling: s(r.selling) })

export const useFxRates = () =>
  useQuery({ queryKey: ['finance', 'fx-rates'], staleTime: 10 * 60_000, queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/fx-rates')).map(mapRate) })

function useFxMut<A, R = unknown>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }) })
}

export const useSaveManualRate = () =>
  useFxMut((i: { currency: string; date: string; buying: string; selling: string }) =>
    api.post('/api/v1/finance/fx-rates', { currency: i.currency, date: i.date, buying: i.buying.replace(',', '.'), selling: (i.selling || i.buying).replace(',', '.') }),
  )

export const useRefreshRates = () => useFxMut(() => api.post<{ tcmb_date?: string; tcmb_count?: number; gold_date?: string; errors?: string[] }>('/api/v1/finance/fx-rates/refresh', {}))

export type Revaluation = {
  accountId: string
  accountName: string
  currency: string
  period: string
  rate: string
  balance: string
  marketTry: string
  bookTry: string
  diffTry: string
  skipped: string
}
const mapReval = (r: Row): Revaluation => ({
  accountId: s(r.account_id),
  accountName: s(r.account_name),
  currency: s(r.currency),
  period: s(r.period),
  rate: s(r.rate),
  balance: s(r.balance),
  marketTry: s(r.market_try),
  bookTry: s(r.book_try),
  diffTry: s(r.diff_try),
  skipped: s(r.skipped),
})

export const useRevaluations = (period: string, enabled = true) =>
  useQuery({
    queryKey: ['finance', 'fx-revaluations', period],
    enabled,
    queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/fx-revaluations', { period })).map(mapReval),
  })

export const useRunRevaluation = () => useFxMut(async (period: string) => asList(await api.post<Row[]>('/api/v1/finance/fx-revaluations', { period })).map(mapReval))

export type Deposit = {
  id: string
  moneyAccountId: string
  accountName: string
  bankName: string
  bankLogoUrl: string
  sourceAccountId: string
  currency: string
  principal: string
  annualRate: string
  dayBasis: number
  startDate: string
  maturityDate: string
  days: number
  daysLeft: number
  withholdingRate: string
  expectedGross: string
  expectedTax: string
  expectedNet: string
  status: 'open' | 'matured' | 'broken'
  grossInterest: string | null
  withholding: string | null
  netInterest: string | null
  closedOn: string
  note: string
}
const mapDeposit = (r: Row): Deposit => ({
  id: s(r.id),
  moneyAccountId: s(r.money_account_id),
  accountName: s(r.account_name),
  bankName: s(r.bank_name),
  bankLogoUrl: s(r.bank_logo_url),
  sourceAccountId: s(r.source_account_id),
  currency: s(r.currency) || 'TRY',
  principal: s(r.principal),
  annualRate: s(r.annual_rate),
  dayBasis: Number(r.day_basis) || 365,
  startDate: s(r.start_date),
  maturityDate: s(r.maturity_date),
  days: Number(r.days) || 0,
  daysLeft: Number(r.days_left) || 0,
  withholdingRate: s(r.withholding_rate),
  expectedGross: s(r.expected_gross),
  expectedTax: s(r.expected_tax),
  expectedNet: s(r.expected_net),
  status: r.status === 'matured' || r.status === 'broken' ? r.status : 'open',
  grossInterest: optS(r.gross_interest),
  withholding: optS(r.withholding),
  netInterest: optS(r.net_interest),
  closedOn: s(r.closed_on),
  note: s(r.note),
})

export const useDeposits = (enabled = true) =>
  useQuery({ queryKey: ['finance', 'deposits'], enabled, queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/deposits')).map(mapDeposit) })

export type DepositInput = {
  sourceAccountId: string
  bankId: string
  name: string
  currency: string
  principal: string
  annualRate: string
  dayBasis: number
  startDate: string
  maturityDate: string
  withholdingRate: string
  note: string
}
const dec = (v: string) => v.trim().replace(',', '.')
export const depositBody = (i: DepositInput) => ({
  source_account_id: i.sourceAccountId,
  bank_id: i.bankId,
  name: i.name.trim(),
  currency: i.currency,
  principal: dec(i.principal) || '0',
  annual_rate: dec(i.annualRate) || '0',
  day_basis: i.dayBasis,
  start_date: i.startDate,
  maturity_date: i.maturityDate,
  withholding_rate: i.withholdingRate.trim() ? dec(i.withholdingRate) : null,
  note: i.note.trim(),
})

export type DepositPreview = { days: number; withholdingRate: string; gross: string; tax: string; net: string; maturityAmount: string }
export const useDepositPreview = (body: ReturnType<typeof depositBody> | null) =>
  useQuery({
    queryKey: ['finance', 'deposit-preview', body],
    enabled: !!body,
    retry: false,
    queryFn: async (): Promise<DepositPreview> => {
      const r = await api.post<Row>('/api/v1/finance/deposits/preview', body)
      return { days: Number(r.days) || 0, withholdingRate: s(r.withholding_rate), gross: s(r.gross), tax: s(r.tax), net: s(r.net), maturityAmount: s(r.maturity_amount) }
    },
  })

export const useOpenDeposit = () => useFxMut((i: DepositInput) => api.post('/api/v1/finance/deposits', depositBody(i)))

export type DepositCloseInput = {
  date: string
  grossInterest: string
  withholding: string
  returnToSource: boolean
  renew: boolean
  renewRate: string
  renewMaturity: string
}
export const useCloseDeposit = () =>
  useFxMut(({ id, broken, input }: { id: string; broken: boolean; input: DepositCloseInput }) =>
    api.post(`/api/v1/finance/deposits/${id}/${broken ? 'break' : 'mature'}`, {
      date: input.date,
      gross_interest: input.grossInterest.trim() ? dec(input.grossInterest) : null,
      withholding: input.withholding.trim() ? dec(input.withholding) : null,
      return_to_source: input.returnToSource,
      renew: input.renew,
      renew_rate: input.renewRate.trim() ? dec(input.renewRate) : null,
      renew_maturity: input.renewMaturity,
    }),
  )

export type WithholdingRate = { id: string; currencyGroup: string; maxMonths: number | null; rate: string; validFrom: string; note: string }
export const useWithholdingRates = (enabled = true) =>
  useQuery({
    queryKey: ['finance', 'withholding-rates'],
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/withholding-rates')).map(
        (r): WithholdingRate => ({
          id: s(r.id),
          currencyGroup: s(r.currency_group),
          maxMonths: r.max_months == null ? null : Number(r.max_months),
          rate: s(r.rate),
          validFrom: s(r.valid_from),
          note: s(r.note),
        }),
      ),
  })

export const useAddWithholdingRate = () =>
  useFxMut((i: { currencyGroup: string; maxMonths: string; rate: string; validFrom: string; note: string }) =>
    api.post('/api/v1/finance/withholding-rates', {
      currency_group: i.currencyGroup,
      max_months: i.maxMonths.trim() ? Number(i.maxMonths) : null,
      rate: dec(i.rate),
      valid_from: i.validFrom,
      note: i.note.trim(),
    }),
  )
